import * as XLSX from 'xlsx'
import { v4 as uuid } from 'uuid'
import type { Expense } from './db'
import { CATEGORIES } from '../pages/record/constants'

export interface ImportRow {
  expense: Omit<Expense, 'created_at' | 'updated_at' | 'deleted_at'>
  duplicate: boolean
}

export interface ImportResult {
  rows: ImportRow[]
  errors: string[]
  statsStartMonth: string | null  // 'YYYY-MM-DD'
}

/** Excel 序列值 or 字串 → 'YYYY-MM-DD' */
function parseDate(raw: unknown): string | null {
  if (raw == null || raw === '') return null
  if (raw instanceof Date) return raw.toISOString().slice(0, 10)
  if (typeof raw === 'number') {
    const d = XLSX.SSF.parse_date_code(raw)
    if (!d) return null
    const mm = String(d.m).padStart(2, '0')
    const dd = String(d.d).padStart(2, '0')
    return `${d.y}-${mm}-${dd}`
  }
  if (typeof raw === 'string') {
    const m = raw.match(/(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/)
    if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
  }
  return null
}

/** 找表頭列（含「日期」「類別」「金額」的那列）*/
function findHeaderRow(sheet: XLSX.WorkSheet): number {
  const range = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1:H1')
  for (let r = range.s.r; r <= Math.min(range.e.r, 20); r++) {
    const a = sheet[XLSX.utils.encode_cell({ r, c: 0 })]?.v
    const b = sheet[XLSX.utils.encode_cell({ r, c: 1 })]?.v
    const d = sheet[XLSX.utils.encode_cell({ r, c: 3 })]?.v
    if (
      String(a ?? '').includes('日期') &&
      String(b ?? '').includes('類別') &&
      String(d ?? '').includes('金額')
    ) return r
  }
  return 3  // 預設第 4 列 (0-based = 3)
}

export function parseExcel(buffer: ArrayBuffer, existing: Expense[]): ImportResult {
  const wb = XLSX.read(buffer, { cellDates: true })

  // 讀統計起始月 總覽!B14
  let statsStartMonth: string | null = null
  const overviewSheet = wb.Sheets['總覽']
  if (overviewSheet) {
    const b14 = overviewSheet['B14']
    if (b14) statsStartMonth = parseDate(b14.v)
  }

  const sheet = wb.Sheets['花費明細']
  if (!sheet) return { rows: [], errors: ['找不到「花費明細」工作表'], statsStartMonth }

  const headerRow = findHeaderRow(sheet)
  const range = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1:H1')
  const rows: ImportRow[] = []
  const errors: string[] = []

  // 建立去重 set
  const existingKeys = new Set(
    existing
      .filter(e => e.deleted_at === null)
      .map(e => `${e.date}|${e.category}|${e.amount}|${e.odometer_km ?? ''}`)
  )

  for (let r = headerRow + 1; r <= range.e.r; r++) {
    const get = (c: number) => sheet[XLSX.utils.encode_cell({ r, c })]?.v

    const rawDate     = get(0)
    const rawCategory = get(1)
    const rawDesc     = get(2)
    const rawAmount   = get(3)
    const rawOdo      = get(4)
    const rawNote     = get(5)

    // 連續空白列 → 結束
    if (rawDate == null && rawAmount == null && rawCategory == null) break

    // 金額空白 → 略過
    if (rawAmount == null || rawAmount === '') continue

    const date = parseDate(rawDate)
    if (!date) { errors.push(`第 ${r + 1} 列：日期無法解析（${rawDate}）`); continue }

    const category = String(rawCategory ?? '').trim()
    if (!(CATEGORIES as readonly string[]).includes(category)) {
      errors.push(`第 ${r + 1} 列：類別「${category}」不在清單內`); continue
    }

    const amount = Math.round(Number(rawAmount))
    if (isNaN(amount) || amount < 0) {
      errors.push(`第 ${r + 1} 列：金額無效（${rawAmount}）`); continue
    }

    const odometer_km = rawOdo != null && rawOdo !== '' ? Math.round(Number(rawOdo)) : null
    const note        = rawNote != null ? String(rawNote).trim() : null

    // 油錢：說明欄如果是 92/95/98 → fuel_grade
    let description = rawDesc != null ? String(rawDesc).trim() : null
    let fuel_grade: string | null = null
    if (category === '油錢') {
      if (['92', '95', '98'].includes(description ?? '')) {
        fuel_grade  = description
        description = description  // 保留原值
      } else {
        fuel_grade = '95'
      }
    }

    const key = `${date}|${category}|${amount}|${odometer_km ?? ''}`
    const duplicate = existingKeys.has(key)

    rows.push({
      duplicate,
      expense: {
        id: uuid(),
        date, category: category as Expense['category'],
        description, amount, odometer_km, note,
        fuel_grade, liters: null, is_full_tank: category === '油錢' ? true : null,
        reminder_rule_id: null,
      },
    })
  }

  return { rows, errors, statsStartMonth }
}
